import { Configuration, IConfiguration } from '@/models/configuration/Configuration';
import { DexieConfiguration } from '@/models/dexie/DexieConfiguration';
import { IDexieConfiguration } from '@/models/dexie/DexieConfiguration';
import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { AxiosService, OnlineService } from '.';
import { Helpers } from '@/helpers/Helpers';
import { ApiEndpoints, ApiIdentifers } from '@/config/index';
import { LoggerService } from '@/utils';
import { ConfigurationSection } from '@/models/configuration/Section';
import { ConfigurationSectionTypeEnum } from '@/enums/ConfigurationSectionTypeEnums';
import { ConfigurationField } from '@/models/configuration/Field';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { AuthStore } from '@/store/store.auth';
import { LiteEvent } from '@/models/event/liteEvent';

class ConfigurationProvider {
    /**
     * Basic configuration for all generalsections
     */
    public configuration: Configuration = new Configuration({ sections: [] });
    /**
     * Contains settings for all processes, e.g. blow room
     */
    public machinesConfiguration: Configuration = new Configuration({ sections: [] });
    /**
     * Contains settings for all samples
     */
    public samplesConfiguration: Configuration = new Configuration({ sections: [] });
    /**
     * Contains configuration for mymill import wizard
     */
    public mymillImportConfiguration: Configuration = new Configuration({ sections: [] });
    /**
     * Contains units definitions
     */
    public unitsConfiguration: Configuration = new Configuration({ sections: [] });
    /**
     * Used to store actual data-set from edit/create view
     */
    public usedConfigurationSections: ConfigurationSection[] = [];
    /**
     * Used to store actual temperature format from edit/create view
     */
    public usedTemperatureFormat: number = 0;

    public completeConfigurationSections: ConfigurationSection[] = [];

    /**
     * TODO: remove when MyIdentity has role for this
     */
    public userWhitelist: string[] = [];
    public userWhitelistLoaded: 'empty' | 'loading' | 'loaded' = 'empty';

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;
    private readonly onUnitsChanged = new LiteEvent<Configuration>();
    private readonly onUserWhitelistChanged = new LiteEvent<string[]>();
    private readonly onUsedTemperatureFormatChanged = new LiteEvent<void>();

    // tslint:disable-next-line: no-empty
    constructor() {}

    /**
     * Method to set usedConfigurationSections
     * @param inSections Array of ConfigurationSection's
     */
    public SetUsedConfigurationSections(inSections: ConfigurationSection[]) {
        this.usedConfigurationSections = [];
        inSections.forEach((sec) => {
            this.usedConfigurationSections.push(new ConfigurationSection(sec));
        });
    }

    /**
     * Clear and reset the used sections
     */
    public ClearUsedConfigurationSections() {
        this.usedConfigurationSections.splice(0, this.usedConfigurationSections.length);
        this.GetConfiguration.sections.forEach((section) => {
            const sectionToAdd = new ConfigurationSection(section);

            /*
             * In case we have an action, we need to put the fields inside a new field
             */
            if (sectionToAdd.action !== undefined) {
                this.generateActionSection(sectionToAdd);
            }

            this.usedConfigurationSections.push(sectionToAdd);
        });
    }

    /**
     * Add or update a section
     * @param inSection The ConfigurationSection object
     */
    public SetUsedConfigurationSection(inSection: ConfigurationSection) {
        let section = this.usedConfigurationSections.find((sec) => sec.identifier === inSection.identifier);
        if (section === undefined) {
            this.usedConfigurationSections.push(inSection);
        } else {
            section = inSection;
        }
    }

    /**
     * Remove a section
     * @param inSection The ConfigurationSection object
     */
    public RemoveUsedConfigurationSection(inSection: ConfigurationSection) {
        const sectionIndex = this.usedConfigurationSections.map((sec) => sec.identifier).indexOf(inSection.identifier);
        if (sectionIndex > -1) {
            this.usedConfigurationSections.splice(sectionIndex, 1);
        }
    }

    /**
     * Method to set the used temperature format.
     * Since temperature format is used in general section
     * and used for all temperature fields, we need to save
     * this seperate -__-.
     *
     * See units configuration values for temperature to
     * get avaiable values.
     * (Should only be °C(0) and °F(1) so far)
     *
     * @param temperatureFormat     A number for temperature format
     */
    public SetUsedTemperatureFormat(temperatureFormat: number) {
        this.usedTemperatureFormat = temperatureFormat;
        this.onUsedTemperatureFormatChanged.trigger();
    }
    /**
     * Clear used temperature format
     */
    public ClearUsedTemperatureFormat() {
        this.usedTemperatureFormat = 0;
    }

    /**
     * Fetch whole configuration file
     */
    public async FetchConfiguration() {
        this.configuration = await this.SyncConfiguration();
    }

    /**
     * Fetch whole configuration file
     */
    public async FetchMymillImportConfiguration() {
        this.mymillImportConfiguration = await this.SyncMymillImportConfiguration();
    }

    /**
     * Fetch whole machinesConfiguration file
     */
    public async FetchMachinesConfiguration() {
        this.machinesConfiguration = await this.SyncMachinesConfiguration();
    }

    /**
     * Fetch whole samplesConfiguration file
     */
    public async FetchSamplesConfiguration() {
        this.samplesConfiguration = await this.SyncSamplesConfiguration();
    }

    /**
     * Fetch whole unitsConfiguration file
     */
    public async FetchUnitsConfiguration() {
        const units = await this.SyncUnitConfiguration();
        this.unitsConfiguration = units;
        this.onUnitsChanged.trigger(units);
    }

    /**
     * Fetch whole userWhitelist file
     * TODO: remove when MyIdentity has role for this
     */
    public async FetchUserWhitelist() {
        if (this.userWhitelistLoaded === 'loaded') {
            this.onUserWhitelistChanged.trigger(this.userWhitelist);
            return;
        } else if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            if (this.userWhitelistLoaded === 'empty') {
                this.userWhitelistLoaded = 'loading';
                await AxiosService.get(ApiEndpoints.configurationUserWhitelist, null)
                    .then(async (response) => {
                        if (response.data != null) {
                            const list = atob(response.data);
                            this.userWhitelist = list.replace(/(\[|\]|\")/g, '').split(',');
                            await this.dexieDB.put(this.dexieDB.getTables().userWhitelist, { id: 1, list: btoa(this.userWhitelist.join()) })
                                .catch((error) => {
                                    LoggerService.LogDebug(`Error on saving to dexiedb: `, error);
                                });
                            this.userWhitelistLoaded = 'loaded';
                            this.onUserWhitelistChanged.trigger(this.userWhitelist);
                        }
                    });
            }
        } else {
            const userWhitelist = this.dexieDB.getTable<{ id?: number, list: string }>(this.dexieDB.getTables().userWhitelist);
            if (userWhitelist !== undefined) {
                await userWhitelist.get({ id: 1 })
                    .then((data) => {
                        if (data) {
                            this.userWhitelist = atob(data.list).split(',');
                            this.userWhitelistLoaded = 'loaded';
                            this.onUserWhitelistChanged.trigger(this.userWhitelist);
                        }
                    });
            }
        }
    }

    /**
     * Method to generate on array out of all configuration sections
     */
    public generateCompleteConfigurationSections() {
        const configSections = new Configuration(this.GetConfiguration).sections;
        const machineSections = new Configuration(this.GetMachinesConfiguration).sections;
        const samplesSections = new Configuration(this.GetSamplesConfiguration).sections;
        machineSections.forEach((machine) => machine.position = machine.position + ApiIdentifers.machinesPositionMultiplier);
        samplesSections.forEach((sample) => sample.position = sample.position + ApiIdentifers.samplesPositionMultiplier);
        this.completeConfigurationSections = [...configSections, ...machineSections, ...samplesSections];
    }

    /**
     * Getter for used configuration
     */
    get GetUsedConfigurationSections() {
        return this.usedConfigurationSections;
    }

    /**
     * Getter for configuration
     */
    get GetConfiguration() {
        return this.configuration;
    }

    /**
     * Getter for mymillImport configuration
     */
    get GetMymillImportConfiguration() {
        return this.mymillImportConfiguration;
    }

    /**
     * Getter for machines configuration
     */
    get GetMachinesConfiguration() {
        return this.machinesConfiguration;
    }

    /**
     * Getter for samples configuration
     */
    get GetSamplesConfiguration() {
        return this.samplesConfiguration;
    }

    /**
     * Getter for units configuration
     */
    get GetUnitsConfiguration() {
        return this.unitsConfiguration;
    }

    /**
     * Getter usedTemperatureFormat
     */
    get GetUsedTemperatureFormat() {
        return this.usedTemperatureFormat;
    }

    /**
     * Getter for userWhitelist
     * TODO: remove when MyIdentity has role for this
     */
    get GetUserWhitelist() {
        return this.userWhitelist;
    }

    /**
     * Getter for complete configuration sections array
     * Attention: do not override this value in any case!
     */
    get GetCompleteConfigurationSections() {
        return this.completeConfigurationSections;
    }

    /**
     * Event getter for unit updates
     */
    public get OnUnitsChanged() {
        return this.onUnitsChanged.expose();
    }

    /**
     * Event getter for unit updates
     */
    public get OnUserWhitelistChanged() {
        return this.onUserWhitelistChanged.expose();
    }

    /**
     * Event getter for unit updates
     */
    public get OnUsedTemperatureFormatChanged() {
        return this.onUsedTemperatureFormatChanged.expose();
    }

    /**
     * Method to generate a default action section.
     * Since an action section can contain multiple isntances of its fields
     * we need to put the fields in sub-fields.
     * @param inSection Configuration section
     */
    public generateActionSection(inSection: ConfigurationSection) {
        const fields = inSection.fields;
        inSection.fields = [];
        inSection.type = ConfigurationSectionTypeEnum.ACTION;
        const field = new ConfigurationField();
        field.position = 0;
        field.name = `${inSection.name}__${field.position}`;
        field.identifier = `${inSection.identifier}__${field.position}`;
        field.type = FieldTypeEnum.ACTION_SUBFIELD;
        field.fields = [];
        field.action = inSection.action;
        field.required = fields[0].required || false;
        inSection.fields.push(field);
        fields.forEach((x: ConfigurationField) => {
            field.fields!.push(new ConfigurationField(x));
        });
    }

    private async SyncConfiguration(): Promise<Configuration> {

        const tableStrings = this.dexieDB.getTables();
        let onlineConf: IConfiguration;
        let dexieConf: IDexieConfiguration;

        const confTable = this.dexieDB.getTable<IDexieConfiguration>(tableStrings.configuration);
        if (confTable !== undefined) {
            await confTable.get({ id: 1 })
                .then((data) => {
                    if (data != null) {
                        dexieConf = new DexieConfiguration(data);
                    }
                });
        }

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            await AxiosService.get(ApiEndpoints.configurationGeneral, null)
                .then((response) => {
                    onlineConf = new Configuration(response.data);
                });
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`,
                OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        const result: Promise<Configuration> = new Promise<Configuration>(async (resolve, reject) => {
            let save = false;
            // check if received an online and offline config
            if (onlineConf !== undefined && dexieConf !== undefined) {
                // check if online config is newer
                if (onlineConf.date
                    && dexieConf.date
                    && Helpers.compareDateStrings(onlineConf.date, dexieConf.date) > 0) {
                    save = true;
                    LoggerService.LogDebug(`Using newer online conf`);
                } else {
                    LoggerService.LogDebug(`Using dexie conf since online conf is not newer`);
                    resolve(dexieConf as Configuration);
                }
            } else if (onlineConf === undefined && dexieConf !== undefined) {
                // check if we only got an offline config
                LoggerService.LogDebug(`Using dexie conf since since no online conf found`);
                resolve(dexieConf as Configuration);
            } else if (onlineConf !== undefined && dexieConf === undefined) {
                // check if we only got an online config
                LoggerService.LogDebug(`Using online conf since since no dexie conf found`);
                save = true;
            } else {
                LoggerService.LogDebug(`Both configuration sources are empty. Are you offline and never synced data before?`);
                reject(`Both configuration sources are empty. Are you offline and never synced data before?`);
            }
            // in case we got only an online config or it is newer save it local first
            if (save) {
                const dexieConfSave: IDexieConfiguration = new DexieConfiguration(onlineConf);
                dexieConfSave.id = 1;
                await this.dexieDB.put(tableStrings.configuration, dexieConfSave)
                    .then(() => {
                        LoggerService.LogDebug(`Using saved online conf`);
                        resolve(onlineConf);
                    })
                    .catch((error) => {
                        LoggerService.LogDebug(`Error on saving to dexiedb: `, error);
                        reject(error);
                    });
            }
        });
        return result;
    }

    private async SyncMymillImportConfiguration(): Promise<Configuration> {

        const tableStrings = this.dexieDB.getTables();
        let onlineMymillImportConf: IConfiguration;
        let dexieMymillImportConf: IDexieConfiguration;

        const confTable = this.dexieDB.getTable<IDexieConfiguration>(tableStrings.mymillImportConfiguration);
        if (confTable !== undefined) {
            await confTable.get({ id: 1 })
                .then((data) => {
                    if (data != null) {
                        dexieMymillImportConf = new DexieConfiguration(data);
                    }
                });
        }

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            await AxiosService.get(ApiEndpoints.configurationMymillImport, null)
                .then((response) => {
                    onlineMymillImportConf = new Configuration(response.data);
                });
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`,
                OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        const result: Promise<Configuration> = new Promise<Configuration>(async (resolve, reject) => {
            let save = false;
            // check if received an online and offline config
            if (onlineMymillImportConf !== undefined && dexieMymillImportConf !== undefined) {
                // check if online config is newer
                if (onlineMymillImportConf.date
                    && dexieMymillImportConf.date
                    && Helpers.compareDateStrings(onlineMymillImportConf.date, dexieMymillImportConf.date) > 0) {
                    save = true;
                    LoggerService.LogDebug(`Using newer online MymillImportConf`);
                } else {
                    LoggerService.LogDebug(`Using dexie MymillImportConf since online MymillImportConf is not newer`);
                    resolve(dexieMymillImportConf as Configuration);
                }
            } else if (onlineMymillImportConf === undefined && dexieMymillImportConf !== undefined) {
                // check if we only got an offline config
                LoggerService.LogDebug(`Using dexie MymillImportConf since since no online MymillImportConf found`);
                resolve(dexieMymillImportConf as Configuration);
            } else if (onlineMymillImportConf !== undefined && dexieMymillImportConf === undefined) {
                // check if we only got an online config
                LoggerService.LogDebug(`Using online MymillImportConf since since no dexie MymillImportConf found`);
                save = true;
            } else {
                LoggerService.LogDebug(`Both configuration sources are empty. Are you offline and never synced data before?`);
                reject(`Both configuration sources are empty. Are you offline and never synced data before?`);
            }
            // in case we got only an online config or it is newer save it local first
            if (save) {
                const dexieConfSave: IDexieConfiguration = new DexieConfiguration(onlineMymillImportConf);
                dexieConfSave.id = 1;
                await this.dexieDB.put(tableStrings.mymillImportConfiguration, dexieConfSave)
                    .then(() => {
                        LoggerService.LogDebug(`Using saved online MymillImportConf`);
                        resolve(onlineMymillImportConf);
                    })
                    .catch((error) => {
                        LoggerService.LogDebug(`Error on saving to dexiedb: `, error);
                        reject(error);
                    });
            }
        });
        return result;
    }

    private async SyncMachinesConfiguration(): Promise<Configuration> {

        const tableStrings = this.dexieDB.getTables();

        let onlineMachinesConf: IConfiguration;
        let dexieMachinesConf: IDexieConfiguration;

        const machinesConfTable = this.dexieDB.getTable<IDexieConfiguration>(tableStrings.machinesConfiguration);
        if (machinesConfTable !== undefined) {
            await machinesConfTable.get({ id: 1 })
                .then((data) => {
                    if (data != null) {
                        dexieMachinesConf = new DexieConfiguration(data);
                    }
                });
        }


        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            await AxiosService.get(ApiEndpoints.configurationMachines, null)
                .then((response) => {
                    onlineMachinesConf = new Configuration(response.data);
                });
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`,
                OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        const result: Promise<Configuration> = new Promise<Configuration>(async (resolve, reject) => {
            let save = false;
            // check if received an online and offline config
            if (onlineMachinesConf !== undefined && dexieMachinesConf !== undefined) {
                // check if online config is newer
                if (onlineMachinesConf.date
                    && dexieMachinesConf.date
                    && Helpers.compareDateStrings(onlineMachinesConf.date, dexieMachinesConf.date) > 0) {
                    save = true;
                    LoggerService.LogDebug(`Using newer online MachinesConf`);
                } else {
                    LoggerService.LogDebug(`Using dexie MachinesConf since online MachinesConf is not newer`);
                    resolve(dexieMachinesConf as Configuration);
                }
            } else if (onlineMachinesConf === undefined && dexieMachinesConf !== undefined) {
                // check if we only got an offline config
                LoggerService.LogDebug(`Using dexie MachinesConf since since no online MachinesConf found`);
                resolve(dexieMachinesConf as Configuration);
            } else if (onlineMachinesConf !== undefined && dexieMachinesConf === undefined) {
                // check if we only got an online config
                LoggerService.LogDebug(`Using online MachinesConf since since no dexie MachinesConf found`);
                save = true;
            } else {
                LoggerService.LogDebug(`Both MachinesConf sources are empty. Are you offline and never synced data before?`);
                reject(`Both MachinesConf sources are empty. Are you MachinesConf and never synced data before?`);
            }
            // in case we got only an online config or it is newer save it local first
            if (save) {
                const dexieConfSave: DexieConfiguration = new DexieConfiguration(onlineMachinesConf);
                dexieConfSave.id = 1;
                await this.dexieDB.put(tableStrings.machinesConfiguration, dexieConfSave)
                    .then(() => {
                        LoggerService.LogDebug(`Using saved online MachinesConf`);
                        resolve(onlineMachinesConf);
                    })
                    .catch((error) => {
                        LoggerService.LogDebug(`Error on saving to dexiedb: `, error);
                        reject(error);
                    });
            }
        });
        return result;
    }

    private async SyncSamplesConfiguration(): Promise<Configuration> {

        const tableStrings = this.dexieDB.getTables();

        let onlineSamplesConf: IConfiguration;
        let dexieSamplesConf: IDexieConfiguration;

        const samplesConfTable = this.dexieDB.getTable<IDexieConfiguration>(tableStrings.samplesConfiguration);
        if (samplesConfTable !== undefined) {
            await samplesConfTable.get({ id: 1 })
                .then((data) => {
                    if (data != null) {
                        dexieSamplesConf = new DexieConfiguration(data);
                    }
                });
        }

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            await AxiosService.get(ApiEndpoints.configurationSamples, null)
                .then((response) => {
                    onlineSamplesConf = new Configuration(response.data);
                });
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`,
                OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        const result: Promise<Configuration> = new Promise<Configuration>(async (resolve, reject) => {
            let save = false;
            // check if received an online and offline config
            if (onlineSamplesConf !== undefined && dexieSamplesConf !== undefined) {
                // check if online config is newer
                if (onlineSamplesConf.date
                    && dexieSamplesConf.date
                    && Helpers.compareDateStrings(onlineSamplesConf.date, dexieSamplesConf.date) > 0) {
                    save = true;
                    LoggerService.LogDebug(`Using newer online SamplesConf`);
                } else {
                    LoggerService.LogDebug(`Using dexie SamplesConf since online SamplesConf is not newer`);
                    resolve(dexieSamplesConf as Configuration);
                }
            } else if (onlineSamplesConf === undefined && dexieSamplesConf !== undefined) {
                // check if we only got an offline config
                LoggerService.LogDebug(`Using dexie SamplesConf since since no online SamplesConf found`);
                resolve(dexieSamplesConf as Configuration);
            } else if (onlineSamplesConf !== undefined && dexieSamplesConf === undefined) {
                // check if we only got an online config
                LoggerService.LogDebug(`Using online SamplesConf since since no dexie SamplesConf found`);
                save = true;
            } else {
                LoggerService.LogDebug(`Both SamplesConf sources are empty. Are you offline and never synced data before?`);
                reject(`Both SamplesConf sources are empty. Are you SamplesConf and never synced data before?`);
            }
            // in case we got only an online config or it is newer save it local first
            if (save) {
                const dexieConfSave: DexieConfiguration = new DexieConfiguration(onlineSamplesConf);
                dexieConfSave.id = 1;
                await this.dexieDB.put(tableStrings.samplesConfiguration, dexieConfSave)
                    .then(() => {
                        LoggerService.LogDebug(`Using saved online SamplesConf`);
                        resolve(onlineSamplesConf);
                    })
                    .catch((error) => {
                        LoggerService.LogDebug(`Error on saving to dexiedb: `, error);
                        reject(error);
                    });
            }
        });
        return result;
    }

    private async SyncUnitConfiguration(): Promise<Configuration> {

        const tableStrings = this.dexieDB.getTables();

        let onlineUnitsConf: IConfiguration;
        let dexieUnitsConf: IDexieConfiguration;

        const unitsConfTable = this.dexieDB.getTable<IDexieConfiguration>(tableStrings.unitsConfiguration);
        if (unitsConfTable !== undefined) {
            await unitsConfTable.get({ id: 1 })
                .then((data) => {
                    dexieUnitsConf = data as IDexieConfiguration;
                });
        }

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE
            && AuthStore.GetIsLoggedIn === true) {
            await AxiosService.get(ApiEndpoints.configurationUnits, null)
                .then((response) => {
                    onlineUnitsConf = new Configuration(response.data);
                });
        } else {
            LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`,
                OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
        }

        const result: Promise<Configuration> = new Promise<Configuration>(async (resolve, reject) => {
            let save = false;
            // check if received an online and offline config
            if (onlineUnitsConf !== undefined && dexieUnitsConf !== undefined) {
                // check if online config is newer
                if (onlineUnitsConf.date
                    && dexieUnitsConf.date
                    && Helpers.compareDateStrings(onlineUnitsConf.date, dexieUnitsConf.date) > 0) {
                    save = true;
                    LoggerService.LogDebug(`Using newer online UnitsConf`);
                } else {
                    LoggerService.LogDebug(`Using dexie UnitsConf since online UnitsConf is not newer`);
                    resolve(dexieUnitsConf as Configuration);
                }
            } else if (onlineUnitsConf === undefined && dexieUnitsConf !== undefined) {
                // check if we only got an offline config
                LoggerService.LogDebug(`Using dexie UnitsConf since since no online UnitsConf found`);
                resolve(dexieUnitsConf as Configuration);
            } else if (onlineUnitsConf !== undefined && dexieUnitsConf === undefined) {
                // check if we only got an online config
                LoggerService.LogDebug(`Using online UnitsConf since since no dexie UnitsConf found`);
                save = true;
            } else {
                LoggerService.LogDebug(`Both UnitsConf sources are empty. Are you offline and never synced data before?`);
                reject(`Both UnitsConf sources are empty. Are you UnitsConf and never synced data before?`);
            }
            // in case we got only an online config or it is newer save it local first
            if (save) {
                const dexieConfSave: DexieConfiguration = new DexieConfiguration(onlineUnitsConf);
                dexieConfSave.id = 1;
                await this.dexieDB.put(tableStrings.unitsConfiguration, dexieConfSave)
                    .then(() => {
                        LoggerService.LogDebug(`Using saved online UnitsConf`);
                        resolve(onlineUnitsConf);
                    })
                    .catch((error) => {
                        LoggerService.LogDebug(`Error on saving to dexiedb: `, error);
                        reject(error);
                    });
            }
        });
        return result;
    }
}

export default ConfigurationProvider;
